import React from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, Star } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { venueImages, venueInfo } from "../lib/i18n";
import { useImages } from "../hooks/useImages";

const Hero = () => {
  const { t } = useLang();
  const { one } = useImages();
  const heroSrc = one("hero", venueImages.interior1);
  return (
    <section
      data-testid="hero-section"
      className="relative pt-52 pb-20 lg:pt-60 lg:pb-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 items-end">
          <div className="col-span-12 lg:col-span-7 order-2 lg:order-1">
            <p className="overline text-[color:var(--wine)] fade-in-up fade-in-up-1 flex items-center">
              <span className="section-line"></span>
              {t.hero.eyebrow}
            </p>

            <h1 className="mt-8 font-serif-display text-[12vw] sm:text-[9vw] lg:text-[7.5vw] leading-[0.92] tracking-[-0.03em]">
              <span className="block fade-in-up fade-in-up-1">{t.hero.title_1}</span>
              <span className="block fade-in-up fade-in-up-2 ml-[10%]">
                <span className="font-serif-accent text-[color:var(--wine)]">{t.hero.title_2}</span>
              </span>
              <span className="block fade-in-up fade-in-up-3 ml-[4%]">
                {t.hero.title_3}
              </span>
            </h1>

            <div className="mt-12 max-w-md text-[color:var(--ink-2)] text-lg leading-relaxed fade-in-up fade-in-up-3">
              {t.hero.subtitle}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4 fade-in-up fade-in-up-4">
              <Link
                to="/menu"
                data-testid="hero-cta-primary"
                className="btn-primary"
              >
                {t.hero.cta_secondary}
                <ArrowDownRight size={16} strokeWidth={1.5} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-secondary"
                className="btn-ghost"
              >
                {t.nav.contact}
              </Link>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5 order-1 lg:order-2 fade-in-up fade-in-up-2">
            <div className="relative aspect-[3/4] overflow-hidden">
              <img
                src={heroSrc}
                alt="Caffè & Cucina Sorriso – Innenbereich"
                data-testid="hero-image"
                className="w-full h-full object-cover smooth-image"
              />
            </div>
            <div className="hidden lg:flex items-center justify-between mt-6 overline text-[color:var(--ink-3)]">
              <span>Sendling · München</span>
              <span className="flex items-center gap-1">
                <Star size={12} strokeWidth={1.5} className="fill-current text-[color:var(--gold)]" />
                <span className="text-[color:var(--wine)]">{venueInfo.rating}</span> · {venueInfo.reviewsCount} reviews
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee strip */}
      <div className="mt-24 lg:mt-32 wine-bg py-6 overflow-hidden">
        <div className="marquee font-serif-display text-3xl sm:text-4xl text-[color:var(--cream)]">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-12 pr-12">
              <span>Pizza Margherita</span>
              <span className="font-serif-accent text-[color:var(--gold)]">·</span>
              <span>Cappuccino</span>
              <span className="font-serif-accent text-[color:var(--gold)]">·</span>
              <span>Lasagne al forno</span>
              <span className="font-serif-accent text-[color:var(--gold)]">·</span>
              <span>Tiramisù</span>
              <span className="font-serif-accent text-[color:var(--gold)]">·</span>
              <span>Cannoli</span>
              <span className="font-serif-accent text-[color:var(--gold)]">·</span>
              <span>Cornetti</span>
              <span className="font-serif-accent text-[color:var(--gold)]">·</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
